import React, { useEffect, useRef, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import ReactSelect from 'react-select'
import _ from 'lodash'

import Lang from 'Lang/General'
import ReorderItemCard from './ReorderItemCard'
import LoadingOverlay from 'components/Indicator/LoadingOverlay'

const ReorderModal = props => {
  const [ jumpToOption, setJumpToOption ] = useState( null )
  const [ scrollRequest, setScrollRequest ] = useState({ id: null, nonce: 0 })

  const reorderDof = ({ oldIndex, newIndex }) => {
    let tmp = _.cloneDeep( props.orderedDigitalOrderForms )
    let tmpNewIndex = newIndex < 0 
      ? 0
      : newIndex > tmp.length - 1
        ? tmp.length - 1
        : newIndex
    const [ removed ] = tmp.splice( oldIndex, 1 )
    tmp.splice( tmpNewIndex, 0, removed )
    props.onChangeDigitalOrderFormHOC( 'orderedDigitalOrderForms', tmp )
  }

  useEffect( () => {
    if ( props.selectedDofToReorderId === 0 ) {
      props.onChangeDigitalOrderFormHOC( 'orderedDigitalOrderForms', [] )
      setJumpToOption( null )
      setScrollRequest({ id: null, nonce: 0 })
    }
  }, [ props.selectedDofToReorderId ])

  return (
    <Modal 
      size='md'
      isOpen={ props.selectedDofToReorderId > 0 } >
      <ModalHeader toggle={ () => props.onChangeDigitalOrderFormHOC( 'selectedDofToReorderId', 0 ) } >
        { Lang[ 'CONFIGURE_ORDER' ][ props.data.languageReducer.lang ] }
      </ModalHeader>
      <ModalBody>
        <style>
          {`
            .reorder-list-sortable-helper {
              z-index: 1060 !important;
              user-select: none !important;
            }
          `}
        </style>
        <ReactSelect
          className="mb-3"
          classNamePrefix="select"
          menuPortalTarget={ document.body }
          styles={{
            menuPortal: base => ({ ...base, zIndex: 9999 }),
            menu: provided => ({ ...provided, zIndex: 9999 })
          }}
          options={ props.orderedDigitalOrderForms }
          value={ jumpToOption }
          isClearable
          placeholder={ Lang[ 'SELECT_FORM' ][ props.data.languageReducer.lang ] }
          onChange={ option => {
            setJumpToOption( option )
            if ( option ) {
              setScrollRequest( prev => ({ id: option.value, nonce: prev.nonce + 1 }) )
            }
          }}
        />
        <SortableList
          axis="y"
          helperClass="reorder-list-sortable-helper"
          helperContainer={ () => document.body }
          onSortStart={ () => {
            if ( window.getSelection ) {
              window.getSelection().removeAllRanges()
            }
          }}
          onSortEnd={ reorderDof }
          items={ props.orderedDigitalOrderForms }
          selectedDofToReorderId={ props.selectedDofToReorderId }
          scrollRequest={ scrollRequest }
          data={ props.data }
          reorderDof={ reorderDof }
          orderedDigitalOrderForms={ props.orderedDigitalOrderForms }
          onChangeDigitalOrderFormHOC={ props.onChangeDigitalOrderFormHOC }
        />
        { props.onLoadDigitalOrderForms && <LoadingOverlay/> }
      </ModalBody>
      <ModalFooter>
        <Button 
          color="primary" 
          onClick={ () => props.updateDigitalOrderFormDisplayOrders(
            props.orderedDigitalOrderForms.map( ( item, index ) => ({ id: item.value, display_order: index + 1 }) )
          )}>
          { Lang[ 'SUBMIT' ][ props.data.languageReducer.lang ] }
        </Button>
      </ModalFooter>
    </Modal>
  )
}

const SortableItem = SortableElement( props => {
  return <ReorderItemCard { ...props } />
})

const SortableList = SortableContainer( props => {
  const listRef = useRef( null )

  useEffect( () => {
    if ( props.selectedDofToReorderId <= 0 || !props.items.length ) {
      return
    }

    let cancelled = false
    let rafId = null

    const tryScrollToSelected = () => {
      if ( cancelled || !listRef.current ) {
        return
      }
      // Wait until every list item node is present in the DOM
      if ( listRef.current.childElementCount < props.items.length ) {
        rafId = requestAnimationFrame( tryScrollToSelected )
        return
      }
      const selectedEl = listRef.current.querySelector(
        `[data-reorder-id="${ props.selectedDofToReorderId }"]`
      )
      if ( selectedEl ) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    rafId = requestAnimationFrame( tryScrollToSelected )

    return () => {
      cancelled = true
      if ( rafId !== null ) {
        cancelAnimationFrame( rafId )
      }
    }
  }, [ props.selectedDofToReorderId ])

  useEffect( () => {
    if ( !props.scrollRequest?.id || !props.items.length ) {
      return
    }

    let cancelled = false
    let rafId = null

    const tryScrollToSelected = () => {
      if ( cancelled || !listRef.current ) {
        return
      }
      if ( listRef.current.childElementCount < props.items.length ) {
        rafId = requestAnimationFrame( tryScrollToSelected )
        return
      }
      const selectedEl = listRef.current.querySelector(
        `[data-reorder-id="${ props.scrollRequest.id }"]`
      )
      if ( selectedEl ) {
        selectedEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }

    rafId = requestAnimationFrame( tryScrollToSelected )

    return () => {
      cancelled = true
      if ( rafId !== null ) {
        cancelAnimationFrame( rafId )
      }
    }
  }, [ props.scrollRequest ])

  return (  
    <div 
      ref={ listRef }
      className='d-flex flex-column w-100'
      style={{ height: "60vh", overflowY: "scroll" }} >
      { 
        props.items.map( ( item, index ) => (
          <SortableItem 
            key={ item.value } 
            index={ index } 
            item={ item } 
            itemIndex={ index }
            selectedDofToReorderId={ props.selectedDofToReorderId }
            orderedDigitalOrderForms={ props.orderedDigitalOrderForms }
            data={ props.data } 
            onChangeDigitalOrderFormHOC={ props.onChangeDigitalOrderFormHOC }
            reorderDof={ props.reorderDof }
          /> 
        )) 
      }
    </div>
  )
})

export default ReorderModal